'use client';

import { type KeyboardEvent, type RefObject, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { normalizeOptionalPhone } from '@/lib/phone-validation';
import type { ProductGiftDetails } from '@/lib/product-gift-schemas';
import { includesGiftRecipientPhone } from '@/lib/product-utils';
import { cn } from '@/lib/utils';
import type { SCAPIShopperProductsSchemas } from 'src/types';

interface ProductGiftSummaryProps {
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>;
    giftDetails: ProductGiftDetails;
    onEdit: () => void;
    onRemove: () => void;
    editButtonRef?: RefObject<HTMLButtonElement | null>;
}

const editButtonClassName = cn(
    'body-copy-m-bold shrink-0 cursor-pointer underline decoration-solid underline-offset-2 text-neutral-10',
    focusVisibleOutline
);

export default function ProductGiftSummary({
    product,
    giftDetails,
    onEdit,
    onRemove,
    editButtonRef,
}: ProductGiftSummaryProps): ReactElement {
    const { t } = useTranslation('product');
    const showPhone = includesGiftRecipientPhone(product);
    const recipientPhone = normalizeOptionalPhone(giftDetails.recipientPhone);

    const handleEditKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onEdit();
        }
    };

    return (
        <div className="flex flex-col gap-3 rounded-md bg-neutral-white p-4" data-testid="product-gift-summary">
            <div className="flex items-start justify-between gap-4">
                <p className="body-copy-m-bold text-neutral-10">{t('gift.summaryTitle')}</p>
                <div className="flex shrink-0 flex-col items-end gap-2">
                    <button
                        ref={editButtonRef}
                        type="button"
                        className={editButtonClassName}
                        onClick={onEdit}
                        onKeyDown={handleEditKeyDown}
                        aria-label={t('gift.editAriaLabel')}>
                        {t('gift.edit')}
                    </button>
                    <button
                        type="button"
                        className={editButtonClassName}
                        onClick={onRemove}
                        aria-label={t('gift.removeAriaLabel')}>
                        {t('gift.remove')}
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex gap-4">
                    <p className="body-copy-m min-w-0 text-neutral-10">{giftDetails.recipientName}</p>
                    <p className="body-copy-m text-neutral-40">{giftDetails.recipientEmail}</p>
                    {showPhone && recipientPhone ? (
                        <p className="body-copy-m text-neutral-40">{recipientPhone}</p>
                    ) : null}
                </div>
                {giftDetails.message ? (
                    <p className="body-copy-m line-clamp-1 text-neutral-10" title={giftDetails.message}>
                        {giftDetails.message}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
